import store from '../configureStore';
import { hasExpired } from './subscriptionDetails';

export const formatDate = (dateStr: string): string => {
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(5, 7);
  const day = dateStr.substring(8, 10);
  return `${day}.${month}.${year}`;
};

export const formatMilliSeconds = (milliSeconds: number | string | undefined): string => {
  const ms = Number(milliSeconds);
  if (ms > 1000) {
    const seconds = ms / 1000;
    if (seconds > 60) {
      const minutes = Math.floor(seconds / 60);
      if (minutes > 60) {
        const hours = Math.floor(minutes / 60);
        const remainderMinutes = Math.floor(minutes - hours * 60);
        const remainderSeconds = Math.floor(seconds - (hours * 60 * 60 + remainderMinutes * 60));
        return `${hours}h${remainderMinutes}m${remainderSeconds}s`;
      }
      const remainderSeconds = Math.floor(seconds - minutes * 60);
      return `${minutes}m${remainderSeconds}s`;
    }
    return `${Math.round((ms / 1000) * 10) / 10}s`;
  }
  return `${Math.round((ms / 1000) * 10) / 10}s`;
};

export const formatBytes = (bytes: number | string | undefined): string => {
  const fileSize = Number(bytes) || 0;
  const kb = fileSize / 1000;
  const mb = fileSize / 1000000;
  const gb = fileSize / 1000000000;
  const tb = fileSize / 1000000000000;

  if (tb >= 1) return `${Math.round(tb * 10) / 10} tb`;
  if (gb >= 1) return `${Math.round(gb * 10) / 10} gb`;
  if (mb >= 1) return `${Math.round(mb * 10) / 10} mb`;
  if (kb >= 1) return `${Math.round(kb * 10) / 10} kb`;
  return `${fileSize} b`;
};

export const getExtFromFileName = (fileName: string): string =>
  fileName.substring(fileName.lastIndexOf('.') + 1);

export const getNameFromFileName = (fileName: string): string =>
  fileName.substring(0, fileName.lastIndexOf('.'));

export function getRelativeTime(current: Date, previous: Date): string {
  const msPerMinute = 60 * 1000;
  const msPerHour = msPerMinute * 60;
  const msPerDay = msPerHour * 24;
  const msPerMonth = msPerDay * 30;
  const msPerYear = msPerDay * 365;
  const elapsed = current.getTime() - previous.getTime();

  const format = (val: number, unit: string) =>
    `${val} ${unit}${val === 1 ? '' : 's'} ago`;

  if (elapsed < msPerMinute) return format(Math.round(elapsed / 1000), 'second');
  if (elapsed < msPerHour) return format(Math.round(elapsed / msPerMinute), 'minute');
  if (elapsed < msPerDay) return format(Math.round(elapsed / msPerHour), 'hour');
  if (elapsed < msPerMonth) return format(Math.round(elapsed / msPerDay), 'day');
  if (elapsed < msPerYear) return format(Math.round(elapsed / msPerMonth), 'month');
  return format(Math.round(elapsed / msPerYear), 'year');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getName = (item: any): string =>
  item.title?.S || item.title?.N || item.title || item.name?.S || item.name?.N || item.name || '';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sortContentList(contentList: any[], sortByCriteriaIndex: number, sortByDirectionIsAscending: boolean) {
  switch (sortByCriteriaIndex) {
    case 0:
      return contentList.sort((a, b) => {
        if (a.created < b.created) return sortByDirectionIsAscending ? 1 : -1;
        if (a.created > b.created) return sortByDirectionIsAscending ? -1 : 1;
        return 0;
      });
    case 1:
      return contentList.sort((a, b) => {
        const nameA = getName(a).toLowerCase();
        const nameB = getName(b).toLowerCase();
        if (nameA < nameB) return sortByDirectionIsAscending ? -1 : 1;
        if (nameA > nameB) return sortByDirectionIsAscending ? 1 : -1;
        return 0;
      });
    case 2:
      return contentList.sort((a, b) => {
        if (a.itemtype === 'resource' && b.itemtype === 'resource') {
          if (a.type < b.type) return sortByDirectionIsAscending ? -1 : 1;
          if (a.type > b.type) return sortByDirectionIsAscending ? 1 : -1;
          return 0;
        }
        if (a.itemtype === 'resource') return sortByDirectionIsAscending ? 1 : -1;
        if (b.itemtype === 'resource') return sortByDirectionIsAscending ? -1 : 1;
        if (a.itemtype < b.itemtype) return sortByDirectionIsAscending ? -1 : 1;
        if (a.itemtype > b.itemtype) return sortByDirectionIsAscending ? 1 : -1;
        return 0;
      });
    default:
      return [];
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sortProjectItemsList(items: any[], sortByCriteriaIndex: number, sortByDirectionIsAscending: boolean) {
  const contentList = [...items];
  switch (sortByCriteriaIndex) {
    case 0:
      return contentList.sort((a, b) => {
        if (a.created < b.created) return sortByDirectionIsAscending ? 1 : -1;
        if (a.created > b.created) return sortByDirectionIsAscending ? -1 : 1;
        return 0;
      });
    case 1:
      return contentList.sort((a, b) => {
        if (a.name < b.name) return sortByDirectionIsAscending ? -1 : 1;
        if (a.name > b.name) return sortByDirectionIsAscending ? 1 : -1;
        return 0;
      });
    case 2:
      return contentList.sort((a, b) => {
        if (a.itemtype === 'resource' && b.itemtype === 'resource') {
          if (a.type < b.type) return sortByDirectionIsAscending ? -1 : 1;
          if (a.type > b.type) return sortByDirectionIsAscending ? 1 : -1;
          return 0;
        }
        if (a.itemtype === 'resource') return sortByDirectionIsAscending ? 1 : -1;
        if (b.itemtype === 'resource') return sortByDirectionIsAscending ? -1 : 1;
        if (a.itemtype < b.itemtype) return sortByDirectionIsAscending ? -1 : 1;
        if (a.itemtype > b.itemtype) return sortByDirectionIsAscending ? 1 : -1;
        return 0;
      });
    default:
      return [];
  }
}

export const getRelativeCoordinates = (event: MouseEvent, element: HTMLElement) => {
  const position = { x: event.pageX, y: event.pageY };
  const offset = { left: element.offsetLeft, top: element.offsetTop };
  let reference = element.offsetParent as HTMLElement | null;
  while (reference != null) {
    offset.left += reference.offsetLeft;
    offset.top += reference.offsetTop;
    reference = reference.offsetParent as HTMLElement | null;
  }
  return { x: position.x - offset.left, y: position.y - offset.top };
};

export const downloadFile = (fileName: string, fileUrl: string): Promise<string> =>
  new Promise((resolve) => {
    const a = document.createElement('a');
    a.style.display = 'none';
    document.body.appendChild(a);
    a.href = fileUrl;
    a.setAttribute('download', fileName);
    a.click();
    window.URL.revokeObjectURL(a.href);
    document.body.removeChild(a);
    resolve('done');
  });

export const addToFavourites = (item: { id: string; [k: string]: unknown }) => {
  const favourites = window.localStorage.getItem('favourites');
  const parsed = favourites ? JSON.parse(favourites) : [];
  window.localStorage.setItem('favourites', JSON.stringify([...parsed, item]));
};

export const removeFromFavourites = (item: { id: string }) => {
  const favourites = window.localStorage.getItem('favourites');
  const parsed: Array<{ id: string }> = favourites ? JSON.parse(favourites) : [];
  window.localStorage.setItem('favourites', JSON.stringify(parsed.filter((f) => f.id !== item.id)));
};

export const createNewVideoElement = (video: { id: string | { S: string }; signedUrl: string; mimeType: string }) => {
  const videoId = typeof video.id === 'string' ? video.id : video.id.S;
  const videoDomElement = document.createElement('video');
  videoDomElement.setAttribute('id', `project-main-view-player-${videoId}`);
  videoDomElement.setAttribute('class', 'video-js');
  videoDomElement.setAttribute('controls', 'true');
  videoDomElement.setAttribute('preload', 'auto');
  videoDomElement.setAttribute('data-setup', '{}');
  videoDomElement.setAttribute('width', '880px');
  videoDomElement.setAttribute('crossorigin', 'anonymous');

  const videoDomSourceElement = document.createElement('source');
  videoDomSourceElement.setAttribute('src', video.signedUrl);
  videoDomSourceElement.setAttribute(
    'type',
    video.mimeType === 'video/quicktime' ? 'video/mp4' : video.mimeType,
  );
  videoDomElement.appendChild(videoDomSourceElement);
  return videoDomElement;
};

export const getValueFromUserAttributes = (
  userAttributes: Array<{ Name: string; Value: string }> | undefined,
  valueName: string,
): string | undefined => {
  if (!userAttributes) return undefined;
  return userAttributes.find((attr) => attr.Name === valueName)?.Value;
};

export const dataURItoBlob = (dataURI: string): Blob => {
  const binary = atob(dataURI.split(',')[1]);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return new Blob([array], { type: 'image/jpeg' });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const checkIfAdminInParentGroup = (currentGroupObj: any, groupsList: any[], currentUserInfo: any): boolean => {
  const parentGroupObj = groupsList.find((group) => group.id.S === currentGroupObj.parent.S);
  if (!parentGroupObj) return false;
  const userGroupParentObj = currentUserInfo.groups.find(
    (group: { id: { S: string } }) => group.id.S === parentGroupObj.id.S,
  );
  return userGroupParentObj?.role?.S === 'admin';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const currentUserIsAdminOfRootGroup = (currentUserInfo: any, groupsList: any[]): boolean => {
  const { workspace: { currentUserGroup } } = store.getState();
  if (!currentUserInfo?.groups || !groupsList?.length) return false;
  const currentUserInfoGroupObj = currentUserInfo.groups.find(
    (group: { id: { S: string } }) => group.id.S === currentUserGroup,
  );
  const currentGroupObj = groupsList.find((group: { id: { S: string } }) => group.id.S === currentUserGroup);
  if (!currentUserInfoGroupObj || !currentGroupObj) return false;
  if (currentUserInfoGroupObj.role.S === 'admin' && currentGroupObj.parent.S === 'root') return true;
  if (currentGroupObj.parent.S !== 'root') {
    return checkIfAdminInParentGroup(currentGroupObj, groupsList, currentUserInfo);
  }
  return false;
};

export const validateEmailFormat = (email: string): boolean => {
  const validateEmailRegEx =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return validateEmailRegEx.test(String(email).toLowerCase());
};

export const imageExists = (imageSrc: string): Promise<Event> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = (data) => resolve(data);
    img.onerror = (err) => reject(err);
    img.src = imageSrc;
  });

export const getLowerCaseFileExtensionInFile = (file: File): File => {
  const fileExt = getExtFromFileName(file.name);
  const fileNameBase = getNameFromFileName(file.name);
  const newCompleteName = `${fileNameBase}.${fileExt.toLowerCase()}`;
  return new File([file], newCompleteName, { type: file.type });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getFullName = (user: any): string => {
  const firstName = user.cognitoAttributes.UserAttributes.find(
    (attr: { Name: string }) => attr.Name === 'given_name',
  )?.Value ?? '';
  const lastName = user.cognitoAttributes.UserAttributes.find(
    (attr: { Name: string }) => attr.Name === 'family_name',
  )?.Value ?? '';
  return `${firstName} ${lastName}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const sortParticipantsListByFullName = (list: any[] | undefined) => {
  if (!list) return undefined;
  return [...list].sort((a, b) => getFullName(a).localeCompare(getFullName(b)));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const sortGroupsListByName = (list: any[]) =>
  list.sort((a, b) => {
    if (a.title.S < b.title.S) return -1;
    if (a.title.S > b.title.S) return 1;
    return 0;
  });

export const capitalize = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getCurrentWorkspace = (groupsList: any[]) => {
  const { workspace: { currentUserGroup } } = store.getState();
  return groupsList.find((group) => group.id.S === currentUserGroup);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const currentWorkspaceHasExpired = (groupsList: any[]): boolean => {
  const currentWorkspace = getCurrentWorkspace(groupsList);
  return hasExpired(currentWorkspace);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const userIsCurrentRootGroupCreator = (groupsList: any[], currentUserInfo: any, currentUserGroup: string): boolean => {
  if (!groupsList) return false;
  const currentGroup = groupsList.find((group) => group.id.S === currentUserGroup);
  if (!currentGroup) return false;
  const isRoot = currentGroup.parent.S === 'root';
  const isAdmin = currentUserInfo?.role?.S === 'admin';
  const isCreator = currentUserInfo?.creator?.N === '1';
  return isRoot && isAdmin && isCreator;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getClosestRootGroup = (groupList: any[], currentGroupId: string) => {
  const elem = groupList.find((group) => group.id.S === currentGroupId);
  if (!elem) return undefined;
  if (elem.parent.S === 'root') return elem;
  return groupList.find((group) => group.id.S === elem.parent.S);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const userIsRootGroupCreator = (currentUserInfo: any, groupsList: any[]): boolean => {
  const rootGroups = groupsList.filter((group) => group.parent.S === 'root');
  return rootGroups.some((group) => group.creator.S === currentUserInfo.user.S);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getCurrentUserCreatorGroup = (currentUserInfo: any, groupsList: any[]) => {
  if (!groupsList || !currentUserInfo) return undefined;
  const rootGroups = groupsList.filter((group) => (group.parent.S || group.parent) === 'root');
  return rootGroups.find(
    (group) => (group.creator.S || group.creator) === (currentUserInfo?.user?.S || currentUserInfo.uid),
  );
};
